import type {
  SelectedWorkspaceVersion,
  WorkspaceDocument,
} from "@/models/workspace/Workspace";

/** The document HTML the viewer asks the API for: one blob of one document. */
export interface DocumentHtmlTarget {
  documentId: string;
  blobId: string;
  revisionVersion: number;
}

/** Whether the route's id names this document: its id, its blob or one of its revisions' blobs. */
export const documentMatchesRoute = (
  document: WorkspaceDocument,
  routeBlobOrId?: string
): boolean =>
  document.blobName === routeBlobOrId ||
  document.id === routeBlobOrId ||
  document.revisions.some((revision) => revision.blobId === routeBlobOrId);

/**
 * Which document HTML to request, taken from the viewer's store — or null while
 * the store describes another document than the URL.
 *
 * The store is persisted, so after a reload, a direct link or a chat citation it
 * still holds the previously opened document until the workspace fetch resolves
 * the new one. Requesting from it downloaded the previous document's whole HTML,
 * then a mix of old and new ids that the API rejects (LAR-487). A store that
 * does describe the URL — the same document reloaded, or a navigation that set
 * it on purpose — lets the request start without waiting for the workspace.
 */
export function resolveDocumentHtmlTarget(
  routeWorkspaceId: string | undefined,
  routeBlobOrId: string | undefined,
  document: WorkspaceDocument | null,
  selectedVersion: SelectedWorkspaceVersion | null
): DocumentHtmlTarget | null {
  if (!routeWorkspaceId || !routeBlobOrId || !document) return null;
  if (document.workspaceId && document.workspaceId !== routeWorkspaceId) {
    return null;
  }
  if (!documentMatchesRoute(document, routeBlobOrId)) return null;

  const isOwnBlob = (blobId: string) =>
    document.blobName === blobId ||
    document.revisions.some((revision) => revision.blobId === blobId);

  if (selectedVersion && isOwnBlob(selectedVersion.blobName)) {
    return {
      documentId: document.id,
      blobId: selectedVersion.blobName,
      revisionVersion: selectedVersion.revisionVersion,
    };
  }

  const routeRevision = document.revisions.find(
    (revision) => revision.blobId === routeBlobOrId
  );
  if (routeRevision) {
    return {
      documentId: document.id,
      blobId: routeRevision.blobId,
      revisionVersion: routeRevision.revisionVersion,
    };
  }

  // The route named the document itself (its id or its current blob).
  return {
    documentId: document.id,
    blobId: document.blobName,
    revisionVersion: document.activeRevision,
  };
}
